import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Table, Alert } from 'react-bootstrap';
import { useInvoice } from '../contexts/InvoiceContext';
import { useAppData } from '../../../providers/AppDataProvider';
import { Invoice, LineAmountType } from '../../../types/accounting';

interface LineItemRow {
  id: string;
  description: string;
  quantity: string;
  unitAmount: string;
}

interface CreateInvoiceModalProps {
  show: boolean;
  onHide: () => void;
  editInvoice?: Invoice | null;
  preSelectedCustomer?: { id: string; name: string; email?: string } | null;
  templateInvoice?: Invoice | null;
}

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const today = isoDay(new Date());
const defaultDueDate = isoDay(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)); // 14 days from now

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

export function CreateInvoiceModal({ show, onHide, editInvoice, preSelectedCustomer, templateInvoice }: CreateInvoiceModalProps) {
  const { createInvoice, createInvoiceLoading, updateInvoice, updateInvoiceLoading, fetchInvoiceDetail, invoiceDetailLoading } = useInvoice();
  const { customers } = useAppData();
  const isEditMode = !!editInvoice;

  const [selectedCustomer, setSelectedCustomer] = useState<{ id: string; name: string; email?: string } | null>(null);
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [dueDate, setDueDate] = useState(defaultDueDate);
  const [reference, setReference] = useState('');
  const [accountCode, setAccountCode] = useState('200');
  const [lineAmountTypes, setLineAmountTypes] = useState<LineAmountType>('Exclusive');
  const [status, setStatus] = useState<'DRAFT' | 'APPROVED'>('APPROVED');
  const [lineItems, setLineItems] = useState<LineItemRow[]>([
    { id: generateId(), description: '', quantity: '1', unitAmount: '' },
  ]);

  const [loadingLineItems, setLoadingLineItems] = useState(false);

  // Validation / error
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Track modal open/close to only initialize once per open
  const prevShowRef = useRef(false);

  useEffect(() => {
    const justOpened = show && !prevShowRef.current;
    prevShowRef.current = show;

    if (!justOpened) return;

    setError(null);
    setSuccess(null);
    setLoadingLineItems(false);

    const populateLineItems = (inv: Invoice, freshIds = false) => {
      if (inv.lineItems && inv.lineItems.length > 0) {
        setLineItems(inv.lineItems.map(li => ({
          id: freshIds ? generateId() : ((li as any).id || generateId()),
          description: li.description || '',
          quantity: String(li.quantity || 1),
          unitAmount: String(li.unitAmount || 0),
        })));
      } else {
        setLineItems([{ id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
      }
    };

    if (editInvoice) {
      setInvoiceDate(editInvoice.date || today);
      setDueDate(editInvoice.dueDate || defaultDueDate);
      setReference(editInvoice.reference || '');
      setLineAmountTypes(editInvoice.lineAmountTypes || 'Exclusive');
      setStatus((editInvoice.status === 'DRAFT' || editInvoice.status === 'APPROVED') ? editInvoice.status : 'APPROVED');
      if (editInvoice.lineItems && editInvoice.lineItems.length > 0) {
        populateLineItems(editInvoice);
      } else {
        setLoadingLineItems(true);
        fetchInvoiceDetail(editInvoice.id).then((full) => {
          if (full) populateLineItems(full);
          setLoadingLineItems(false);
        }).catch(() => setLoadingLineItems(false));
      }
    } else if (templateInvoice) {
      setInvoiceDate(today);
      setDueDate(defaultDueDate);
      setReference(templateInvoice.reference || '');
      setLineAmountTypes(templateInvoice.lineAmountTypes || 'Exclusive');
      setStatus('APPROVED');
      setAccountCode('200');
      setSelectedCustomer(
        templateInvoice.contactId
          ? { id: templateInvoice.contactId, name: templateInvoice.contactName || '', email: templateInvoice.contactEmail ?? undefined }
          : null
      );
      if (templateInvoice.lineItems && templateInvoice.lineItems.length > 0) {
        populateLineItems(templateInvoice, true);
      } else {
        setLoadingLineItems(true);
        fetchInvoiceDetail(templateInvoice.id).then((full) => {
          if (full) populateLineItems(full, true);
          setLoadingLineItems(false);
        }).catch(() => setLoadingLineItems(false));
      }
    } else {
      setSelectedCustomer(preSelectedCustomer ?? null);
      setInvoiceDate(today);
      setDueDate(defaultDueDate);
      setReference('');
      setAccountCode('200');
      setLineAmountTypes('Exclusive');
      setStatus('APPROVED');
      setLineItems([{ id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
    }
  }, [show, editInvoice, templateInvoice, fetchInvoiceDetail, preSelectedCustomer]);

  // Line item handlers
  const addLineItem = () => {
    setLineItems(prev => [...prev, { id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
  };

  const removeLineItem = (id: string) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  const updateLineItem = (id: string, field: keyof LineItemRow, value: string) => {
    setLineItems(prev =>
      prev.map(item => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const subtotal = lineItems.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const unit = parseFloat(item.unitAmount) || 0;
    return sum + qty * unit;
  }, 0);

  const gstAmount = lineAmountTypes === 'NoTax' ? 0
    : lineAmountTypes === 'Inclusive' ? Math.round((subtotal / 11) * 100) / 100
    : Math.round((subtotal * 0.1) * 100) / 100;

  const invoiceTotal = lineAmountTypes === 'Inclusive' ? subtotal
    : lineAmountTypes === 'NoTax' ? subtotal
    : subtotal + gstAmount;

  const validate = (): string | null => {
    if (!isEditMode && !selectedCustomer) {
      return 'Please select a customer.';
    }
    if (lineItems.length === 0) {
      return 'At least one line item is required.';
    }
    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.description.trim()) {
        return `Line item ${i + 1} requires a description.`;
      }
      const qty = parseFloat(item.quantity);
      if (isNaN(qty) || qty <= 0) {
        return `Line item ${i + 1} requires a valid quantity.`;
      }
      const unit = parseFloat(item.unitAmount);
      if (isNaN(unit)) {
        return `Line item ${i + 1} requires a valid unit amount.`;
      }
    }
    return null;
  };

  // Submit
  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      if (isEditMode && editInvoice) {
        const result = await updateInvoice({
          invoiceId: editInvoice.id,
          date: invoiceDate,
          dueDate,
          reference: reference.trim() || undefined,
          status,
          lineAmountTypes,
          lineItems: lineItems.map(item => ({
            description: item.description.trim(),
            quantity: parseFloat(item.quantity),
            unitAmount: parseFloat(item.unitAmount),
            taxType: lineAmountTypes === 'NoTax' ? 'NONE' as const : 'GST' as const,
            accountCode: accountCode.trim() || undefined,
          })),
        });
        setSuccess(`Invoice ${result.invoice?.invoiceNumber || editInvoice.invoiceNumber} updated successfully!`);
        setTimeout(() => onHide(), 1500);
      } else {
        const result = await createInvoice({
          contactId: selectedCustomer!.id,
          contactName: selectedCustomer!.name,
          contactEmail: selectedCustomer!.email,
          reference: reference.trim() || undefined,
          date: invoiceDate,
          dueDate,
          status,
          lineAmountTypes,
          lineItems: lineItems.map(item => ({
            description: item.description.trim(),
            quantity: parseFloat(item.quantity),
            unitAmount: parseFloat(item.unitAmount),
            taxType: lineAmountTypes === 'NoTax' ? 'NONE' as const : 'GST' as const,
            accountCode: accountCode.trim() || undefined,
          })),
        });
        setSuccess(`Invoice ${result.invoice?.invoiceNumber} created successfully!`);
        setTimeout(() => onHide(), 1500);
      }
    } catch (e: any) {
      setError(e?.message || `Failed to ${isEditMode ? 'update' : 'create'} invoice.`);
    }
  };
  
  const isCopyMode = !isEditMode && !!templateInvoice;
  const isLoading = isEditMode ? updateInvoiceLoading : createInvoiceLoading;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditMode
            ? `Edit Invoice ${editInvoice?.invoiceNumber || ''}`
            : isCopyMode
              ? `Copy Invoice ${templateInvoice?.invoiceNumber || ''}`
              : 'Create Invoice'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {isEditMode && editInvoice?.status === 'PAID' && (
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            This invoice has been paid. Some fields may not be editable.
          </Alert>
        )}

        <Form>
          {/* Customer */}
          <Form.Group className="mb-3">
            <Form.Label>Customer</Form.Label>
            {isEditMode ? (
              <Form.Control
                type="text"
                value={editInvoice?.contactName || 'Unknown Customer'}
                disabled
                className="bg-secondary text-muted"
              />
            ) : (
              <Form.Select
                value={selectedCustomer?.id || ''}
                onChange={(e) => {
                  const id = e.target.value;
                  const c = customers.find((x: any) => x.id === id);
                  setSelectedCustomer(c && c.id ? { id: c.id, name: c.name || c.company || '', email: c.email } : null);
                }}
              >
                <option value="">Select a customer</option>
                {customers.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.company || c.id}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          {/* Dates */}
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Invoice Date</Form.Label>
                <Form.Control
                  type="date"
                  value={invoiceDate}
                  onChange={e => setInvoiceDate(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Due Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>

          {/* Reference */}
          <Form.Group className="mb-3">
            <Form.Label>Reference (optional)</Form.Label>
            <Form.Control
              type="text"
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder="e.g., PO number, job reference"
            />
          </Form.Group>

          {/* GST Treatment + Status */}
          <div className="row">
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>GST Treatment</Form.Label>
                <Form.Select
                  value={lineAmountTypes}
                  onChange={(e) => setLineAmountTypes(e.target.value as LineAmountType)}
                >
                  <option value="Exclusive">GST Exclusive (prices are ex GST)</option>
                  <option value="Inclusive">GST Inclusive (prices include GST)</option>
                  <option value="NoTax">No Tax</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'DRAFT' | 'APPROVED')}
                  disabled={isEditMode && editInvoice?.status !== 'DRAFT' && editInvoice?.status !== 'APPROVED'}
                >
                  <option value="APPROVED">Awaiting Payment</option>
                  <option value="DRAFT">Draft</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Account Code (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={accountCode}
                  onChange={(e) => setAccountCode(e.target.value)}
                  placeholder="e.g., 200"
                />
              </Form.Group>
            </div>
          </div>

          {/* Line Items */}
          <Form.Group className="mb-3">
            <Form.Label>
              Line Items
              {loadingLineItems && (
                <span className="ms-2 text-muted small">
                  <span className="spinner-border spinner-border-sm me-1" role="status" />
                  Loading line items...
                </span>
              )}
            </Form.Label>
            <Table bordered size="sm" className="mb-2">
              <thead>
                <tr>
                  <th style={{ width: '50%' }}>Description</th>
                  <th style={{ width: '15%' }}>Qty</th>
                  <th style={{ width: '20%' }}>Unit Price</th>
                  <th style={{ width: '15%' }}>Amount</th>
                  <th style={{ width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => {
                  const qty = parseFloat(item.quantity) || 0;
                  const unit = parseFloat(item.unitAmount) || 0;
                  const amount = qty * unit;
                  return (
                    <tr key={item.id}>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={item.description}
                          onChange={e => updateLineItem(item.id, 'description', e.target.value)}
                          placeholder="Description"
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity}
                          onChange={e => updateLineItem(item.id, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          value={item.unitAmount}
                          onChange={e => updateLineItem(item.id, 'unitAmount', e.target.value)}
                          placeholder="0.00"
                        />
                      </td>
                      <td className="align-middle text-end">{formatCurrency(amount)}</td>
                      <td className="align-middle text-center">
                        {lineItems.length > 1 && (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => removeLineItem(item.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="text-end fw-bold">
                    Subtotal {lineAmountTypes === 'Inclusive' ? '(inc GST)' : lineAmountTypes === 'NoTax' ? '' : '(ex GST)'}
                  </td>
                  <td className="fw-bold text-end">{formatCurrency(subtotal)}</td>
                  <td></td>
                </tr>
                {lineAmountTypes !== 'NoTax' && (
                  <tr>
                    <td colSpan={3} className="text-end text-muted">
                      {lineAmountTypes === 'Inclusive' ? 'Includes GST of' : 'GST (10%)'}
                    </td>
                    <td className="text-end text-muted">{formatCurrency(gstAmount)}</td>
                    <td></td>
                  </tr>
                )}
                <tr>
                  <td colSpan={3} className="text-end fw-bold">Total</td>
                  <td className="fw-bold text-end">{formatCurrency(invoiceTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
            <Button variant="outline-primary" size="sm" onClick={addLineItem}>
              <i className="bi bi-plus-lg me-1"></i>
              Add Line Item
            </Button>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading || loadingLineItems}>
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              {isEditMode ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            isEditMode ? 'Update Invoice' : 'Create Invoice'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CreateInvoiceModal;
